import { useMemo } from "react";
import styled from "@emotion/styled";
import { useSearchParams } from "react-router-dom";
import type { SlotStatus } from "@clustec/common";
// Value import via the subpath: the root barrel re-exports the drizzle schema and would drag
// `postgres` into the browser bundle. Types alone are erased, so line 4 can stay on the root.
import { deriveMissCauses } from "@clustec/common/slot-status";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";
import { useChainHealth, useProductionStats } from "../api/hooks";
import { useNetworkStore } from "../stores/network";
import {
  Card,
  Loading,
  PageContainer,
  PageTitle,
  StatLabel,
  StatValue,
  Table,
  TableWrapper,
} from "../components/ui";
import { CollapsibleSection } from "../components/CollapsibleSection";
import {
  SlotStatusLabel,
  orderedStatusCounts,
  slotStatusColor,
} from "../components/SlotStatusLabel";
import { MissRunHistogram } from "../components/MissRunHistogram";
import { EpochTimeline } from "../components/EpochTimeline";
import { RangeToolbar } from "../components/RangeToolbar";
import { Pagination, usePagedRows } from "../components/Pagination";
import { HexDisplay } from "../components/HexDisplay";
import { CopyAddressList } from "../components/CopyAddressList";

/**
 * Chain health: why slots were missed, and by whom.
 *
 * One scrolling page rather than tabs, with collapsible sections carrying the length on mobile.
 * Everything below the window selector comes from a single `production/stats` call — deliberately
 * without `include=slots`, which can be thousands of rows that the epoch timeline already
 * covers visually.
 *
 * The window lives in the URL so a link pasted into an incident channel reopens the same view,
 * and so search can deep-link a slot or epoch straight here.
 */

/**
 * Zoom levels, finest first. "epoch" is the only one that isn't trailing hours: picking it moves
 * the window onto a single epoch, where the toolbar's arrows step one epoch at a time.
 *
 * It has to be a selectable range rather than an implied mode. The arrows only appear once an
 * epoch is selected, so with nothing but hour windows on offer the epoch view was reachable only
 * by deep link — from a dashboard strip column, the worst-epoch link, or a hand-typed `?epoch=`.
 * Landing on Health directly, there was no way in at all.
 */
const EPOCH_RANGE = "epoch";

const WINDOWS: { key: string; label: string; mobileHidden?: boolean }[] = [
  { key: EPOCH_RANGE, label: "epoch" },
  { key: "24", label: "24h" },
  { key: "72", label: "3d" },
  { key: "168", label: "7d" },
  { key: "720", label: "30d", mobileHidden: true },
];

const INCLUDES = ["attestations", "runs", "trend", "l1gas", "proposers", "attesters"];

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing.sm};
`;

const WindowCaption = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing.md};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};

  ${media.sm} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SummaryCard = styled(Card)`
  padding: ${theme.spacing.sm};
`;

/** Single stacked bar of the cause split — the shape of the failure at a glance. */
const CauseBar = styled.div`
  display: flex;
  height: 12px;
  border-radius: 3px;
  overflow: hidden;
  background: ${theme.colors.bgHover};
  margin-bottom: ${theme.spacing.sm};
`;

const CauseSlice = styled.div<{ color: string; pct: number }>`
  width: ${(p) => p.pct}%;
  background: ${(p) => p.color};
  min-width: ${(p) => (p.pct > 0 ? "2px" : "0")};
`;

const BarCaption = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing.sm};
`;

const CauseRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const CauseRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.fontSize.sm};
`;

const CauseCount = styled.span`
  margin-left: auto;
  font-family: monospace;
  color: ${theme.colors.textMuted};
  flex-shrink: 0;
`;

/**
 * Caveats about how much of the window we can actually speak to. Rendered on the panel they
 * qualify rather than in a tooltip: the API reports these counts precisely so thin data reads
 * as thin, and hiding them would put back the false confidence they exist to prevent.
 */
const Caveat = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.warning};
  margin-top: ${theme.spacing.sm};
  line-height: 1.5;
`;

const Muted = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  line-height: 1.5;
`;

const EmptyState = styled.div`
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.textMuted};
  line-height: 1.6;
  padding: ${theme.spacing.sm} 0;
`;

/**
 * A section's explainer with its list-level action beside it. The section header is itself a
 * button (it collapses), so a control cannot live up there without nesting one button in another;
 * this row is the first thing inside the body instead, which keeps it above the table it acts on.
 */
const ExplainerRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};

  > *:last-child {
    flex-shrink: 0;
    margin-left: auto;
  }

  ${media.sm} {
    flex-direction: column;
    gap: ${theme.spacing.xs};

    > *:last-child {
      margin-left: 0;
    }
  }
`;

const TwoUp = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};

  ${media.md} {
    grid-template-columns: 1fr;
  }
`;

/**
 * Miss trend, as a plotted area with a labelled y-axis.
 *
 * It used to be bare bars normalised to the window's own peak, coloured blue below 5% missed and
 * red above, with a 2% minimum height. Four things made that unreadable: a window peaking at 0.4%
 * drew exactly like one peaking at 40%, so no bar height meant anything without hovering; the
 * axis row printed first/peak/last values rather than axis ticks, so a bar could not be read off
 * a scale; a clean bucket still drew a stub, making zero look like a small miss; and the blue/red
 * split matched neither the cause legend nor `slotStatusColor`, which the rest of the page uses.
 *
 * Now: gridlines with tick labels so a bar can be read off a scale, and each bar stacked by the
 * causes that made it up, in `slotStatusColor`.
 *
 * The y-axis picks the smallest ceiling from a fixed ladder that contains the peak, rather than
 * scaling continuously to it. Continuous auto-scaling is what made a 0.4% window indistinguishable
 * from a 40% one. A single hard ceiling has the opposite failure — pinned at 25%, a window peaking
 * at 1% drew 3px of bar and was just as unreadable — so the ladder keeps the axis honest while
 * still filling the box. The steps are coarse and always labelled, so a changed scale is visible
 * at a glance instead of silently redefining what a bar height means.
 */
const TREND_CEILING_LADDER = [1, 2.5, 5, 10, 25, 50, 100];

function trendCeiling(peakPct: number): number {
  return TREND_CEILING_LADDER.find((c) => peakPct <= c) ?? 100;
}

const TrendPlot = styled.div`
  position: relative;
  height: 72px;
  border-bottom: 1px solid ${theme.colors.border};
`;

/** Gridline at a fraction of the ceiling, with its value labelled in the gutter. */
const Gridline = styled.div<{ atPct: number }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${(p) => p.atPct}%;
  border-top: 1px dashed ${theme.colors.border};
  opacity: 0.5;
`;

/**
 * Tick in the right-hand gutter. The ceiling tick is rendered at full contrast, not muted: the
 * axis maximum moves with the data, so it is the one number a reader must take in before reading
 * any bar height. Stating it in prose beneath the chart is not "at a glance" — it belongs on the
 * axis it describes.
 */
const TickLabel = styled.span<{ atPct: number; strong?: boolean }>`
  position: absolute;
  right: 0;
  bottom: ${(p) => p.atPct}%;
  transform: translateY(50%);
  padding-left: 4px;
  font-family: monospace;
  font-size: ${theme.fontSize.xs};
  color: ${(p) => (p.strong ? theme.colors.text : theme.colors.textMuted)};
  background: ${theme.colors.bg};
  line-height: 1;
`;

const TrendBars = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  /* Tight, because the endpoint now sizes buckets from the span: a window that used to arrive as
     one bar can arrive as three dozen, and 3px of gap between 4px bars overflowed the panel. */
  gap: 2px;
`;

/** One bucket. Height is its miss rate against the fixed ceiling; zero draws nothing. */
const TrendBar = styled.div<{ h: number }>`
  flex: 1;
  min-width: 3px;
  height: ${(p) => p.h}%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border-radius: 2px 2px 0 0;
  overflow: hidden;
`;

const TrendSlice = styled.span<{ pct: number; color: string }>`
  display: block;
  width: 100%;
  height: ${(p) => p.pct}%;
  background: ${(p) => p.color};
`;

/** Marks a bucket whose rate exceeded the ceiling, so a clamp never reads as the true value. */
const TrendClamp = styled.span`
  display: block;
  width: 100%;
  height: 3px;
  background: ${theme.colors.danger};
  box-shadow: 0 0 0 1px ${theme.colors.bg};
`;

const AxisRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing.xs};
`;

/** Colour key for the histogram's stacks — the causes actually present, nothing more. */
const RunLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs} ${theme.spacing.md};
  margin-top: ${theme.spacing.sm};
`;

const NumCell = styled.td`
  text-align: right;
  font-family: monospace;
  white-space: nowrap;
`;

const NumHead = styled.th`
  text-align: right;
`;

const pct2 = (n: number) => `${n.toFixed(2)}%`;

/**
 * Axis tick: the shortest exact rendering. `Number(...)` drops trailing zeros without the string
 * surgery that ate the leading digit of "0" and left a bare "%" on the zero tick.
 */
const tickPct = (n: number) => `${Number(n.toFixed(2))}%`;

/**
 * A trend bucket's own label. `from` means different things per bucket — an epoch number when
 * grouping by epoch, unix seconds otherwise — so it cannot be rendered without knowing which,
 * and printing it raw would show an epoch number as if it were a timestamp.
 *
 * Sub-day buckets carry the date as well as the clock time. The endpoint now sizes buckets from
 * the window's span, so a 6h bucket can appear in a 7-day selection where four bare "06:00"
 * labels would be four different days.
 */
function bucketLabel(from: number, trend?: { unit: string; bucketSeconds: number | null }): string {
  if (trend?.unit === "epoch") return `epoch ${from}`;
  const d = new Date(from * 1000);
  const width = trend?.bucketSeconds ?? 86400;
  return width < 86400
    ? d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

/**
 * The bucket width, said the way a reader would say it. The caption used to print the endpoint's
 * `groupBy` value ("per day"), which was only ever right because the endpoint always bucketed by
 * day; it now picks a width from the selection's span, and "per bucket" would tell nobody how
 * wide a column is.
 */
function bucketWidthLabel(trend?: { unit: string; bucketSeconds: number | null }): string {
  if (trend?.unit === "epoch") return "epoch";
  const width = trend?.bucketSeconds;
  if (width == null) return "bucket";
  if (width === 60) return "minute";
  if (width === 3600) return "hour";
  if (width === 86400) return "day";
  if (width === 604800) return "week";
  if (width < 3600) return `${Math.round(width / 60)}m`;
  if (width < 86400) return `${Math.round(width / 3600)}h`;
  return `${Math.round(width / 86400)}d`;
}

export function Health() {
  const { selectedNetwork } = useNetworkStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // An explicit epoch wins over trailing hours: the endpoint accepts exactly one selector, so
  // the UI has to pick one too rather than sending both and getting a 400.
  const epochParam = searchParams.get("epoch");
  const epoch = epochParam != null && epochParam !== "" ? parseInt(epochParam, 10) : null;
  const hours = parseInt(searchParams.get("hours") ?? "24", 10);

  const { data: health } = useChainHealth(selectedNetwork);
  const { data: stats, isLoading } = useProductionStats(selectedNetwork, {
    epoch: epoch ?? undefined,
    hours: epoch == null ? hours : undefined,
    include: INCLUDES,
  });

  // Only consumed by the epoch timeline, which renders for an explicit ?epoch= selection only.
  const { data: epochHealth } = useChainHealth(selectedNetwork, epoch ?? undefined);
  const currentEpoch = health?.currentEpochNumber ?? null;
  // Every window states the epoch range it resolved to, so a time-based selection can still be
  // compared against the Dashboard, which speaks only in epochs.
  const epochDurationSlots = stats?.protocol.epochDurationSlots ?? null;
  const isLiveTimeline = epoch === currentEpoch;

  const setWindow = (next: { epoch?: number | null; hours?: number }) => {
    const params = new URLSearchParams(searchParams);
    if (next.hours != null) {
      params.set("hours", String(next.hours));
      params.delete("epoch");
    }
    if (next.epoch === null) params.delete("epoch");
    else if (next.epoch != null) params.set("epoch", String(next.epoch));
    setSearchParams(params, { replace: true });
  };

  // Picking "epoch" enters epoch mode on the live one, which is the only epoch we can name before
  // the user has said otherwise; the arrows take it from there. Ignored until the live epoch is
  // known, rather than guessing 0 and sending the reader to genesis.
  const selectRange = (key: string) => {
    if (key !== EPOCH_RANGE) return setWindow({ hours: parseInt(key, 10) });
    if (currentEpoch != null) setWindow({ epoch: currentEpoch });
  };

  const causes = deriveMissCauses(stats?.statusCounts);
  // The bar is drawn against the classified slots, not the missed total. Normalising to the
  // missed total makes it permanently full in alarm colours however few slots failed — the fill
  // has to be the miss rate to mean anything. Same denominator as missedPct so the two agree.
  const classifiedSlots = (stats?.totals.mined ?? 0) + (stats?.totals.missed ?? 0);

  // Baseline pinned first: the diagnostic value of this table is comparing a miss cause against
  // checkpoint-mined, so it must never sort below the fold.
  const attestationRows = useMemo(() => {
    const profile = stats?.attestationProfile;
    if (!profile) return [];
    return (Object.keys(profile) as SlotStatus[]).sort((a, b) => {
      if (a === "checkpoint-mined") return -1;
      if (b === "checkpoint-mined") return 1;
      return (profile[b]?.slots ?? 0) - (profile[a]?.slots ?? 0);
    });
  }, [stats]);

  // Both validator lists arrive capped at 200 and sorted worst-first, so paging is purely a
  // DOM-size concern — the rows that matter are always on page one.
  const proposerRows = stats?.proposers ?? [];
  const attesterList = stats?.attesters?.validators ?? [];
  const proposerPage = usePagedRows(proposerRows, 25);
  const attesterPage = usePagedRows(attesterList, 25);
  // Copied from the full lists rather than the visible page, and in the tables' own worst-first
  // order, so the paste arrives ranked the way the reader was reading it.
  const proposerAddresses = useMemo(() => proposerRows.map((p) => p.address), [proposerRows]);
  const attesterAddresses = useMemo(() => attesterList.map((v) => v.address), [attesterList]);

  // Causes that actually produced a run, in the histogram's own stacking order, so the legend
  // beneath it lists exactly the colours on screen — mined runs excluded, as there they are
  // healthy streaks rather than bursts.
  const missRunStatuses = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [status, lengths] of Object.entries(stats?.runLengths ?? {})) {
      if (status === "checkpoint-mined") continue;
      counts[status] = Object.values(lengths).reduce((a, b) => a + b, 0);
    }
    // Narrowed back to real statuses. `orderedStatusCounts` now also ranks the derived `orphaned`
    // bucket, but run lengths are computed by a gaps-and-islands query over stored `slot_status`
    // values, so that bucket cannot appear here — and SlotStatusLabel draws its explainer from
    // SLOT_STATUS_INFO, which has no entry for something that isn't a status.
    return orderedStatusCounts(counts)
      .map(([status]) => status)
      .filter((status): status is SlotStatus => status !== "orphaned");
  }, [stats]);

  const trendPoints = stats?.trend?.points ?? [];
  // Peak of the observed buckets, with no floor: a window that genuinely never missed should
  // report 0%, not be told its peak was 1% because that was the smallest number worth drawing.
  const trendMax = trendPoints.length > 0 ? Math.max(...trendPoints.map((p) => p.missedPct)) : 0;
  const trendCeilingPct = trendCeiling(trendMax);
  const l1ByStatus = stats?.l1Gas?.byStatus ?? {};
  const l1Statuses = Object.keys(l1ByStatus) as SlotStatus[];

  return (
    <PageContainer>
      <Header>
        <PageTitle style={{ margin: 0 }}>Chain Health</PageTitle>
        <RangeToolbar
          ranges={WINDOWS}
          activeRange={epoch != null ? EPOCH_RANGE : String(hours)}
          onRangeSelect={selectRange}
          canGoBack={epoch != null && epoch > 0}
          canGoForward={epoch != null && currentEpoch != null && epoch < currentEpoch}
          onGoBack={() => epoch != null && setWindow({ epoch: epoch - 1 })}
          onGoForward={() => epoch != null && setWindow({ epoch: epoch + 1 })}
          // In epoch mode, "latest" means the live epoch — not an exit back to trailing hours,
          // which is what it did while epoch mode had no button of its own.
          onGoToLatest={() =>
            setWindow(
              epoch != null && currentEpoch != null ? { epoch: currentEpoch } : { epoch: null },
            )
          }
          showArrows={epoch != null}
        />
      </Header>

      <WindowCaption>
        {epoch != null ? (
          <>
            Epoch {epoch}
            {epoch === currentEpoch ? " (current)" : ""}
          </>
        ) : (
          <>Trailing {hours}h</>
        )}
        {stats && (
          <>
            {" · slots "}
            {stats.query.fromSlot}–{stats.query.toSlot}
          </>
        )}
        {epoch == null && stats && epochDurationSlots != null && (
          <>
            {" · epochs "}
            {Math.floor(stats.query.fromSlot / epochDurationSlots)}–
            {Math.floor(stats.query.toSlot / epochDurationSlots)}
          </>
        )}
      </WindowCaption>

      {isLoading && <Loading />}

      {stats && (
        <>
          <CollapsibleSection
            id="health-summary"
            title="Summary"
            summary={
              `${stats.totals.missed} missed · ${pct2(stats.totals.missedPct)}` +
              (stats.totals.orphaned > 0 ? ` · ${stats.totals.orphaned} discarded` : "")
            }
          >
            <SummaryGrid>
              <SummaryCard>
                <StatLabel>Mined</StatLabel>
                <StatValue>{stats.totals.mined}</StatValue>
              </SummaryCard>
              <SummaryCard>
                <StatLabel>Missed</StatLabel>
                <StatValue>{stats.totals.missed}</StatValue>
              </SummaryCard>
              <SummaryCard>
                <StatLabel>Missed %</StatLabel>
                <StatValue>{pct2(stats.totals.missedPct)}</StatValue>
              </SummaryCard>
              {/* Only shown when non-zero. On the overwhelming majority of windows no pending
                  chain was ever discarded, and a permanent zero here would be noise beside four
                  figures that always mean something. */}
              {stats.totals.orphaned > 0 && (
                <SummaryCard>
                  <StatLabel>Discarded by reorg</StatLabel>
                  <StatValue style={{ color: theme.colors.orphaned }}>
                    {stats.totals.orphaned}
                  </StatValue>
                </SummaryCard>
              )}
              <SummaryCard>
                <StatLabel>No verdict</StatLabel>
                <StatValue>{stats.totals.indeterminate}</StatValue>
              </SummaryCard>
            </SummaryGrid>

            {/* One picture of the selection's slots, never two.

                For an explicit epoch the timeline is strictly the better of the pair — the same
                slots, in order, each one hoverable — while the bar is its own aggregate, so
                showing both asked the reader to reconcile a summary with the thing it summarised
                and to work out that they were the same 32 slots. The bar earns its place only
                where a per-slot view cannot exist: a trailing window spans thousands of slots. */}
            {epoch != null &&
              (epochHealth?.epochTimeline ? (
                <>
                  <EpochTimeline
                    epochTimeline={epochHealth.epochTimeline}
                    genesisTime={epochHealth.genesisTime}
                    slotDuration={epochHealth.slotDuration}
                    isLive={isLiveTimeline}
                  />
                  {/* Deliberately short: the timeline states its own epoch, its live state and
                      its mined/missed split in its header, and repeats the tap-for-detail hint
                      underneath. All this has to add is what the counts below it are. */}
                  <BarCaption style={{ marginTop: theme.spacing.sm }}>
                    Every slot in the epoch, oldest first. Counts below break the misses down.
                  </BarCaption>
                </>
              ) : (
                <EmptyState>No slot data for this epoch.</EmptyState>
              ))}

            {epoch == null && causes.length > 0 && (
              <>
                <CauseBar
                  title={`${stats.totals.missed} of ${classifiedSlots} classified slots missed`}
                >
                  {/* The mined share is drawn, not left as the bar's own track. Unfilled grey is
                      the colour of absent data everywhere else on this page, so a bar that was
                      99% track read as "nothing observed" rather than "these slots were fine" —
                      exactly backwards for the status that means success. */}
                  <CauseSlice
                    color={slotStatusColor("checkpoint-mined")}
                    pct={classifiedSlots > 0 ? (stats.totals.mined / classifiedSlots) * 100 : 0}
                    title={`checkpoint-mined: ${stats.totals.mined}`}
                  />
                  {causes.map((c) => (
                    <CauseSlice
                      key={c.status}
                      color={slotStatusColor(c.status)}
                      pct={classifiedSlots > 0 ? (c.count / classifiedSlots) * 100 : 0}
                      title={`${c.status}: ${c.count}`}
                    />
                  ))}
                </CauseBar>
                <BarCaption>
                  Full bar is the {classifiedSlots} classified slots: {stats.totals.mined} mined,
                  then the miss split — {pct2(stats.totals.missedPct)} of the window. Counts below
                  break the misses down.
                </BarCaption>
              </>
            )}

            {causes.length > 0 ? (
              <CauseRows>
                {causes.map((c) => (
                  <CauseRow key={c.status}>
                    <SlotStatusLabel status={c.status} />
                    <CauseCount>
                      {c.count} · {c.pctOfMissed.toFixed(1)}%
                    </CauseCount>
                  </CauseRow>
                ))}
              </CauseRows>
            ) : (
              <EmptyState>No missed slots in this {epoch != null ? "epoch" : "window"}.</EmptyState>
            )}

            <Muted style={{ marginTop: theme.spacing.md }}>
              {stats.coverage.slotsWithStatus} of {stats.coverage.slotsInSelection} slots have a
              recorded status
              {stats.coverage.sentinelOldestSlot != null && (
                <>
                  {" · sentinel observed "}
                  {stats.coverage.sentinelOldestSlot}–{stats.coverage.sentinelNewestSlot}
                </>
              )}
            </Muted>
            {stats.coverage.slotsMissing > 0 && (
              <Caveat>
                {stats.coverage.slotsMissing} slots here have no recorded status. Slots below the
                sentinel's oldest observed slot were evicted before we could see them and are
                unrecoverable; anything above it is a gap in our own polling.
              </Caveat>
            )}
          </CollapsibleSection>

          <CollapsibleSection
            id="health-attestations"
            title="Attestation profile"
            summary={`${attestationRows.length} statuses`}
          >
            <Muted style={{ marginBottom: theme.spacing.sm }}>
              Attestations observed per slot, by status. A miss cause whose profile matches the
              checkpoint-mined baseline was not an attestation failure — the committee did its job
              and the slot failed elsewhere.
            </Muted>
            {attestationRows.length > 0 ? (
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <NumHead>Slots</NumHead>
                      <NumHead>Sent min</NumHead>
                      <NumHead>Sent avg</NumHead>
                      <NumHead>Sent max</NumHead>
                      <NumHead>Missed avg</NumHead>
                    </tr>
                  </thead>
                  <tbody>
                    {attestationRows.map((status) => {
                      const row = stats.attestationProfile?.[status];
                      if (!row) return null;
                      const isBaseline = status === "checkpoint-mined";
                      return (
                        <tr
                          key={status}
                          style={
                            isBaseline
                              ? { background: theme.colors.bgHover, fontWeight: 600 }
                              : undefined
                          }
                        >
                          <td>
                            <SlotStatusLabel status={status} />
                            {isBaseline && <Muted style={{ marginTop: 2 }}>baseline</Muted>}
                          </td>
                          <NumCell>{row.slots}</NumCell>
                          <NumCell>{row.sentMin ?? "–"}</NumCell>
                          <NumCell>{row.sentAvg ?? "–"}</NumCell>
                          <NumCell>{row.sentMax ?? "–"}</NumCell>
                          <NumCell>{row.missedAvg ?? "–"}</NumCell>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrapper>
            ) : (
              <EmptyState>No attestation data in this window.</EmptyState>
            )}
          </CollapsibleSection>

          <TwoUp>
            <CollapsibleSection
              id="health-trend"
              title="Miss trend"
              summary={
                trendPoints.length > 1
                  ? `${pct2(trendPoints[0].missedPct)} → ${pct2(
                      trendPoints[trendPoints.length - 1].missedPct,
                    )}`
                  : undefined
              }
            >
              {trendPoints.length > 0 ? (
                <>
                  <Muted style={{ marginBottom: theme.spacing.sm }}>
                    Missed share per {bucketWidthLabel(stats.trend)}, stacked by cause.
                  </Muted>
                  <TrendPlot>
                    {[25, 50, 75, 100].map((frac) => (
                      <Gridline key={frac} atPct={frac} />
                    ))}
                    <TrendBars>
                      {trendPoints.map((p) => {
                        const causes = deriveMissCauses(p.statusCounts);
                        const clamped = p.missedPct > trendCeilingPct;
                        const height = Math.min(p.missedPct / trendCeilingPct, 1) * 100;
                        return (
                          <TrendBar
                            key={p.from}
                            h={height}
                            title={
                              `${bucketLabel(p.from, stats.trend)} — ${p.missed} missed of ` +
                              `${p.slotsObserved} · ${pct2(p.missedPct)}` +
                              (clamped ? ` (above the ${trendCeilingPct}% ceiling)` : "") +
                              (causes.length > 0
                                ? "\n" + causes.map((c) => `${c.status}: ${c.count}`).join("\n")
                                : "")
                            }
                          >
                            {clamped && <TrendClamp />}
                            {causes.map((c) => (
                              <TrendSlice
                                key={c.status}
                                pct={c.pctOfMissed}
                                color={slotStatusColor(c.status)}
                              />
                            ))}
                          </TrendBar>
                        );
                      })}
                    </TrendBars>
                    {[0, 50, 100].map((frac) => (
                      <TickLabel key={frac} atPct={frac} strong={frac === 100}>
                        {tickPct((trendCeilingPct * frac) / 100)}
                      </TickLabel>
                    ))}
                  </TrendPlot>
                  <AxisRow>
                    <span>{bucketLabel(trendPoints[0].from, stats.trend)}</span>
                    <span>peak {pct2(trendMax)}</span>
                    <span>
                      {bucketLabel(trendPoints[trendPoints.length - 1].from, stats.trend)}
                    </span>
                  </AxisRow>
                </>
              ) : (
                <EmptyState>Not enough data to plot a trend.</EmptyState>
              )}
            </CollapsibleSection>

            {/* "Consecutive misses", not "Consecutive runs": mined runs are excluded — a streak
                of successful slots is not a run of anything worth reporting here — so the old
                title named the mechanism (gaps-and-islands over equal statuses) rather than the
                thing on screen. */}
            <CollapsibleSection
              id="health-runs"
              title="Consecutive misses"
              summary={missRunStatuses.length > 0 ? undefined : "none"}
            >
              <Muted style={{ marginBottom: theme.spacing.sm }}>
                How misses cluster. Isolated singles point at individual proposers; a bump at one
                length, or anything far out along the axis, points at something systemic.
              </Muted>
              {missRunStatuses.length > 0 ? (
                <>
                  <MissRunHistogram runLengths={stats.runLengths ?? {}} />
                  {/* The colour key, since the columns are stacked by cause. Same component as the
                      summary legend, so a colour and a name pair the same way in both. */}
                  <RunLegend>
                    {missRunStatuses.map((status) => (
                      <SlotStatusLabel key={status} status={status} showHelp={false} />
                    ))}
                  </RunLegend>
                </>
              ) : (
                <EmptyState>
                  No missed slots in this window, so there are no runs to plot.
                </EmptyState>
              )}
            </CollapsibleSection>
          </TwoUp>

          <CollapsibleSection id="health-l1" title="L1 conditions">
            <Muted style={{ marginBottom: theme.spacing.sm }}>
              L1 base fee at the slots of each status. A checkpoint that was attested but never
              published failed on the L1 submission path, so fees at those slots against the mined
              baseline is the first thing to check.
            </Muted>
            {l1Statuses.length > 0 ? (
              <TableWrapper>
                <Table>
                  {/* Grouped so the unit is stated once per group rather than repeated on four
                      headers or — as before — left off entirely. Every fee figure here is gwei;
                      the endpoint converts from wei. */}
                  <thead>
                    <tr>
                      <th rowSpan={2}>Status</th>
                      <NumHead rowSpan={2}>Sampled</NumHead>
                      <NumHead colSpan={3}>Base fee (gwei)</NumHead>
                      <NumHead rowSpan={2}>Blob median (gwei)</NumHead>
                    </tr>
                    <tr>
                      <NumHead>Min</NumHead>
                      <NumHead>Median</NumHead>
                      <NumHead>Max</NumHead>
                    </tr>
                  </thead>
                  <tbody>
                    {l1Statuses.map((status) => {
                      const row = l1ByStatus[status];
                      const slots = stats.attestationProfile?.[status]?.slots;
                      const thin = slots != null && row.slotsWithSample < slots;
                      return (
                        <tr key={status}>
                          <td>
                            <SlotStatusLabel status={status} showHelp={false} />
                          </td>
                          <NumCell style={thin ? { color: theme.colors.warning } : undefined}>
                            {row.slotsWithSample}
                            {slots != null && ` / ${slots}`}
                          </NumCell>
                          <NumCell>{row.baseFeeGweiMin ?? "–"}</NumCell>
                          <NumCell>{row.baseFeeGweiMedian ?? "–"}</NumCell>
                          <NumCell>{row.baseFeeGweiMax ?? "–"}</NumCell>
                          <NumCell>{row.blobBaseFeeGweiMedian ?? "–"}</NumCell>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrapper>
            ) : (
              <EmptyState>
                No L1 gas samples cover this window. Sampling began with the pricing sampler, so
                earlier windows have none.
              </EmptyState>
            )}
            {stats.l1Gas && (
              <Muted style={{ marginTop: theme.spacing.sm }}>
                A slot only counts when a sample exists within {stats.l1Gas.maxSampleAgeSeconds}s
                before it — a highlighted sample count means thin coverage, not calm fees.
              </Muted>
            )}
          </CollapsibleSection>

          <CollapsibleSection
            id="health-proposers"
            title="Proposers"
            summary={
              stats.proposers?.length
                ? `${stats.proposers.length} attributed`
                : "no attribution yet"
            }
          >
            <ExplainerRow>
              <Muted>
                The elected sequencer per slot, from the node's sentinel — not the coinbase address
                a proposer chooses for fees.
              </Muted>
              <CopyAddressList addresses={proposerAddresses} noun="proposers" />
            </ExplainerRow>
            {proposerRows.length > 0 ? (
              <>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <th>Address</th>
                        <NumHead>Assigned</NumHead>
                        <NumHead>Missed</NumHead>
                        <NumHead>Missed %</NumHead>
                        <NumHead>Last slot</NumHead>
                      </tr>
                    </thead>
                    <tbody>
                      {proposerPage.visible.map((p) => (
                        <tr key={p.address}>
                          <td>
                            <HexDisplay address={p.address} link={false} />
                          </td>
                          <NumCell>{p.slotsAssigned}</NumCell>
                          <NumCell>{p.missed}</NumCell>
                          <NumCell>{p.missedPct.toFixed(1)}%</NumCell>
                          <NumCell>{p.lastSlot}</NumCell>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
                <Pagination
                  page={proposerPage.page}
                  pageCount={proposerPage.pageCount}
                  from={proposerPage.from}
                  to={proposerPage.to}
                  total={proposerPage.total}
                  onPage={proposerPage.setPage}
                  note={proposerPage.total >= 200 ? "worst 200 shown" : undefined}
                />
              </>
            ) : (
              <EmptyState>
                No slot in this window has a recorded proposer. Proposer capture began with the
                current indexer; slots before that were evicted from the sentinel's history before
                we could attribute them, and cannot be recovered.
              </EmptyState>
            )}
            {stats.unattributedSlots != null && stats.unattributedSlots > 0 && (
              <Caveat>
                {stats.unattributedSlots} slots in this window have no proposer recorded and are
                excluded from the figures above.
              </Caveat>
            )}
          </CollapsibleSection>

          <CollapsibleSection
            id="health-attesters"
            title="Attesters"
            summary={
              stats.attesters?.validators.length
                ? `${stats.attesters.validators.length} validators`
                : "no roster yet"
            }
          >
            <ExplainerRow>
              <Muted>
                Attestation reliability per validator, measured against the epoch committee roster.
              </Muted>
              <CopyAddressList addresses={attesterAddresses} noun="validators" />
            </ExplainerRow>
            {stats.attesters && stats.attesters.validators.length > 0 ? (
              <>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <th>Address</th>
                        <NumHead>Seated</NumHead>
                        <NumHead>Missed</NumHead>
                        <NumHead>Missed %</NumHead>
                        <NumHead>Last missed</NumHead>
                      </tr>
                    </thead>
                    <tbody>
                      {attesterPage.visible.map((v) => (
                        <tr key={v.address}>
                          <td>
                            <HexDisplay address={v.address} link={false} />
                          </td>
                          <NumCell>{v.seatedSlots}</NumCell>
                          <NumCell>{v.missed}</NumCell>
                          <NumCell>{v.missedPct.toFixed(2)}%</NumCell>
                          <NumCell>{v.lastMissedSlot ?? "–"}</NumCell>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
                <Pagination
                  page={attesterPage.page}
                  pageCount={attesterPage.pageCount}
                  from={attesterPage.from}
                  to={attesterPage.to}
                  total={attesterPage.total}
                  onPage={attesterPage.setPage}
                  note={attesterPage.total >= 200 ? "worst 200 shown" : undefined}
                />
                <Muted style={{ marginTop: theme.spacing.sm }}>
                  Based on {stats.attesters.slotsCounted} slots.
                </Muted>
              </>
            ) : (
              <EmptyState>
                No committee roster covers this window. Roster capture began with the current
                indexer, and the sentinel evicts its history, so earlier epochs have no denominator
                to measure against.
              </EmptyState>
            )}
            {stats.attesters &&
              (stats.attesters.slotsWithoutRoster > 0 || stats.attesters.slotsInconsistent > 0) && (
                <Caveat>
                  {stats.attesters.slotsWithoutRoster > 0 && (
                    <div>
                      {stats.attesters.slotsWithoutRoster} slots excluded — no roster recorded for
                      their epoch.
                    </div>
                  )}
                  {stats.attesters.slotsInconsistent > 0 && (
                    <div>
                      {stats.attesters.slotsInconsistent} slots excluded — the roster disagrees with
                      the attestation count, so the sentinel data for them was incomplete.
                    </div>
                  )}
                </Caveat>
              )}
          </CollapsibleSection>
        </>
      )}
    </PageContainer>
  );
}
